import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap, filter, map, take } from 'rxjs';
import { UserSettings } from '../models/user-settings';
import { Store } from '@ngrx/store';
import { ApplicationState } from '@store/application-store';
import { selectUserSettingsState } from '@user-settings/selectors/user-settings';
import { State as UserSettingsState } from '@user-settings/reducers/user-settings';
import * as withUserSettingsActions from '@user-settings/actions/user-settings';
import { MojoWellKnownNames } from '@common/constants/mojo-well-known-names';

@Injectable({
  providedIn: 'root'
})
export class UserSettingsService {

  private userSettings = new BehaviorSubject<UserSettings>(null);

  constructor(
    private readonly store: Store<ApplicationState>
  ) { }

  getUserSettings(): Observable<UserSettings> {
    return this.store.select(selectUserSettingsState)
      .pipe(
        tap((userSettings: UserSettingsState) => {
          if (!userSettings.loading && !userSettings.loaded && !userSettings.error) {
            this.store.dispatch(new withUserSettingsActions.Load());
          } else {
            this.userSettings.next(userSettings.userSettings);
          }
        }),
        filter(userSettings => userSettings != undefined && userSettings.loaded),
        map(u => u.userSettings)
      );
  }

  getUserSettingsImmediate(): UserSettings {
    return this.userSettings.value ;
  }

  getCurrentBrand(): Observable<string> {
    return this.getUserSettings().pipe(map(state => state.BrandName));
  }

  getCurrentProduct(): Observable<string> {
    return this.getUserSettings().pipe(map(state => state.Product));
  }

  isProductMojoDashboard(settings: UserSettings): boolean {
    return settings.Product === MojoWellKnownNames.Products.MojoDashboard;
  }

  isUsingInternalProduct(): Observable<boolean> {
    return this.getUserSettings().pipe(map(this.isProductMojoDashboard));
  }

  userHasAccessToAddOn(addOnCode: string): Observable<boolean> {
    return this.getUserSettings().pipe(
      take(1),
      map(state => {
        const val = state.EnabledAddOns.find(x => x.toLowerCase() === addOnCode.toLowerCase()) != null;
        return val;
      })
    );
  }
}
